/* @jsx jsx */

import { css, jsx } from "@emotion/react"
import {
  Body,
  Button,
  CircledChevron,
  CTA,
  H1,
  H4,
  moduleIntro,
  Page,
  PageComponent,
  Section,
} from "@src/components"
import Layout from "@src/components/layout"
import { breakpoints, colors, desktop, mobile } from "@src/style"
import { compose, shuffle, slice } from "lodash/fp"
import { range as _range, shuffle as _shuffle } from "lodash"
import { Fragment, useCallback, useEffect, useState } from "react"
import { IoMdClose } from "react-icons/io"
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  TelegramIcon,
  TelegramShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from "react-share"
import { shuffle as shuffleImage } from "@src/assets/images"

export const MeningPage: (locale: "nl" | "en") => React.FC<any> = locale => {
  return ({ data, pageContext: { idFromRequest } }: any) => {
    const [meningen, setMeningen] = useState([])
    const [provincieMeningen, setProvincieMeningen] = useState<
      typeof data.allGoogleSpreadsheetMeningen.edges
    >([])
    const [selectedMeningId, setSelectedMeningId] = useState<string | null>(
      null
    )
    const [selectedMeningIndex, setSelectedMeningIndex] = useState<
      number | null
    >(null)
    const [randomValues, setRandomValues] = useState<number[]>(_range(8))
    const [provincies, setProvincies] = useState([
      { name: "groningen", selected: false },
      { name: "friesland", selected: false },
      { name: "drenthe", selected: false },
      { name: "overijssel", selected: false },
      { name: "flevoland", selected: false },
      { name: "gelderland", selected: false },
      { name: "utrecht", selected: false },
      { name: "noord-holland", selected: false },
      { name: "zuid-holland", selected: false },
      { name: "zeeland", selected: false },
      { name: "noord-brabant", selected: false },
      { name: "limburg", selected: false },
    ])
    const [showShare, setShowShare] = useState(false)
    const [showProvincies, setShowProvincies] = useState(false)
    const [initialId, setInitialId] = useState(idFromRequest)
    const [isMobile, setIsMobile] = useState(false)

    useEffect(() => {
      function handleResize() {
        if (window.innerWidth < breakpoints.desktop) {
          setIsMobile(true)
        } else {
          setIsMobile(false)
        }
      }
      window.addEventListener("resize", handleResize)
      handleResize()
      return () => window.removeEventListener("resize", handleResize)
    }, [])

    useEffect(() => {
      if (isMobile && data.allGoogleSpreadsheetMeningen.edges.length) {
        handleThumbClick(
          shuffle(data.allGoogleSpreadsheetMeningen.edges)[0].node.id
        )
      }
    }, [isMobile])

    useEffect(() => {
      const selectedProvincies = getSelectedProvincies(provincies)
      if (!selectedProvincies.length) {
        setProvincieMeningen(data.allGoogleSpreadsheetMeningen.edges)
      } else {
        setProvincieMeningen(
          data.allGoogleSpreadsheetMeningen.edges.filter(mening => {
            const meningProvincie = mening.node.provincie
            if (!meningProvincie) {
              return false
            }
            return selectedProvincies
              .map(provincie => provincie.name.toLowerCase())
              .includes(meningProvincie.toLowerCase())
          })
        )
      }
    }, [provincies])
    const updateRandomValues = useCallback(() => {
      setRandomValues(_shuffle(randomValues))
    }, [randomValues])
    useEffect(() => {
      updateRandomValues()
    }, [meningen])
    useEffect(() => {
      updateMeningen()
    }, [provincieMeningen])
    useEffect(() => {
      if (selectedMeningId === null) {
        if (typeof initialId === "undefined") {
          // console.log("ja huh")
          window.history.replaceState(null, "", `/meningen/`)
          setSelectedMeningIndex(null)
        }
        return
      }

      window.history.replaceState(null, "", `/meningen/${selectedMeningId}`)
    }, [selectedMeningId])

    useEffect(() => {
      if (typeof initialId !== "undefined" && provincieMeningen.length) {
        setInitialId(undefined)
        handleThumbClick(idFromRequest)
      }
    }, [initialId, provincieMeningen])

    if (!data.datoCmsMeningenPage) {
      return (
        <Layout locale="nl">Maak aub de meningen page aan in DatoCMS</Layout>
      )
    }
    const { title, bodyNode, content } = data.datoCmsMeningenPage
    const updateMeningen = () => {
      setMeningen(compose(slice(0, 8), shuffle)(provincieMeningen))
    }
    const selectNextMening = () => {
      if (selectedMeningIndex === null) {
        return
      }
      if (selectedMeningIndex < provincieMeningen.length - 1) {
        const newIndex = selectedMeningIndex + 1
        window.history.replaceState(
          null,
          "",
          `/meningen/${provincieMeningen[newIndex].node.id}`
        )
        setSelectedMeningIndex(newIndex)
      }
    }
    const selectPreviousMening = () => {
      if (selectedMeningIndex === null) {
        return
      }
      if (selectedMeningIndex <= 0) {
        return
      }
      const newIndex = selectedMeningIndex - 1
      window.history.replaceState(
        null,
        "",
        `/meningen/${provincieMeningen[newIndex].node.id}`
      )
      setSelectedMeningIndex(newIndex)
    }
    function handleProvincieClick(
      provincies: { name: string; selected: boolean }[],
      provincie: { name: string; selected: boolean }
    ) {
      return () =>
        setProvincies(
          provincies.map(testProvincie => {
            if (testProvincie.name === provincie.name) {
              return {
                ...provincie,
                selected: !provincie.selected,
              }
            }
            return { ...testProvincie, selected: false }
          })
        )
    }
    function handleThumbClick(id: string | null | undefined) {
      const index = provincieMeningen.reduce(
        (acc: number | null, val: any, i: number) => {
          // console.log(val.node.id, id)
          if (val.node.id === id) {
            return i
          }
          return acc
        },
        null as number | null
      )
      if (index === null || !provincieMeningen[index]) {
        return
      }

      setProvincieMeningen(
        [provincieMeningen[index]].concat(
          index > 0 ? provincieMeningen.slice(0, index) : [],
          index < provincieMeningen.length - 1
            ? provincieMeningen.slice(index + 1)
            : []
        )
      )
      setSelectedMeningIndex(0)
      if (typeof selectedMeningId !== "undefined") {
        setSelectedMeningId(id!)
      }
    }
    return (
      <Layout locale={locale} headerColor="green">
        {moduleIntro(title, bodyNode, "green")}

        <div
          className="hide-desktop"
          css={css`
            background-color: ${colors.green};
            padding: 0px;
          `}
        >
          <div
            css={css`
              text-align: center;
            `}
          >
            <CTA
              onClick={() => setShowProvincies(!showProvincies)}
              direction="down"
              css={css`
                background: ${colors.white};
                padding: 16px;
                width: 227px;
              `}
            >
              Kies een provincie
            </CTA>
          </div>
          {showProvincies && (
            <div
              css={css`
                background: ${colors.white};
                padding: 45px 18px 80px;
                position: relative;
                width: 227px;
                ${mobile} {
                  position: absolute;
                  left: 50%;
                  transform: translateX(-50%);
                  z-index: 999;
                }
              `}
            >
              {provincies.map((provincie, i) => (
                <div
                  key={`provincie-${i}`}
                  css={css`
                    cursor: pointer;
                    font-family: Snv Cond D Fixed;
                    font-size: 25px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 18px;
                    letter-spacing: -0.02em;
                    text-align: left;
                    text-transform: uppercase;
                    margin-bottom: 8px;
                    color: ${provincie.selected ? colors.black : colors.grey};
                    &:hover {
                      text-decoration: underline;
                    }
                  `}
                  onClick={handleProvincieClick(provincies, provincie)}
                >
                  {provincie.name}
                </div>
              ))}
            </div>
          )}
        </div>
        <div
          css={css`
            background: ${colors.green};
          `}
        >
          <div
            css={css`
              position: relative;
              display: flex;
              min-height: 57vw;
              z-index: 1;
              padding: 150px 90px 50px 0;
              ${mobile} {
                /* padding: 50px; */
                padding: 16px;
              }
            `}
          >
            <Fragment>
              <div
                css={css`
                  flex: 1;
                  position: relative;
                  ${mobile} {
                    display: none;
                  }
                `}
              >
                {meningen.map(({ node: { id, thumbnail } }, i: number) => {
                  const size = 30 + i * 5
                  return (
                    <div
                      key={`mening-${i}`}
                      // href={`/meningen/${id}`}
                      onClick={() => handleThumbClick(id)}
                      css={css`
                        padding-bottom: 28%;
                        width: 21%;
                        display: block;
                        position: absolute;
                        left: ${(randomValues[i] % 4) * 25}%;
                        top: ${(randomValues[i] > 3 ? 50 : 0) +
                        (randomValues[i] % 2) * -10}%;
                        cursor: pointer;
                        transition: all 0.6s;
                        animation: sway-parent ${5 + i * 0.1}s linear 0s
                          infinite ${i % 2 ? "reverse" : "normal"};
                        animation-delay: -${i}s;
                        &:hover {
                          opacity: 1;
                          z-index: ${meningen.length + 2};
                        }
                      `}
                    >
                      <div
                        css={css`
                          position: absolute;
                          width: 100%;
                          height: 100%;
                          top: 3px;
                          left: 3px;
                          animation: sway-child ${5 + i * 0.1}s linear 0s
                            infinite ${i % 2 ? "reverse" : "normal"};
                          animation-delay: -${i}s;
                        `}
                      >
                        <div
                          css={css`
                            position: absolute;
                            width: 100%;
                            height: 100%;
                            border-radius: 4px;
                            overflow: hidden;
                            filter: drop-shadow(
                              -5px 5px 15px rgba(0, 0, 0, 0.25)
                            );
                            &:hover {
                              transform: scale(1.1);
                            }
                          `}
                        >
                          <div
                            // src={thumbnail!}
                            // alt=""
                            css={css`
                              height: 100%;
                              display: block;
                              width: 100%;
                              position: absolute;
                              top: 0;
                              left: 0;
                              background-image: url(${thumbnail!});
                              background-position: center bottom;
                              background-size: cover;
                            `}
                          />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  justify-content: flex-start;
                  margin-left: 120px;
                  align-items: flex-end;
                  z-index: ${meningen.length + 1};
                  ${mobile} {
                    display: none;
                  }
                `}
              >
                <H4
                  css={css`
                    text-align: right;
                    margin-bottom: 24px;
                    color: ${colors.white};
                  `}
                >
                  {provincieMeningen.length} meningen
                </H4>
                <CTA
                  onClick={() => setShowProvincies(!showProvincies)}
                  direction="down"
                  css={css`
                    background: ${colors.white};
                    padding: 16px;
                    width: 227px;
                  `}
                >
                  Kies een provincie
                </CTA>
                {showProvincies && (
                  <div
                    css={css`
                      background: ${colors.white};
                      padding: 45px 18px 80px;
                      position: relative;
                      width: 227px;
                    `}
                  >
                    {provincies.map((provincie, i) => (
                      <div
                        key={`provincie-${i}`}
                        css={css`
                          cursor: pointer;
                          font-family: Snv Cond D Fixed;
                          font-size: 25px;
                          font-style: normal;
                          font-weight: 400;
                          line-height: 18px;
                          letter-spacing: -0.02em;
                          text-align: left;
                          text-transform: uppercase;
                          margin-bottom: 8px;
                          color: ${provincie.selected
                            ? colors.black
                            : colors.grey};
                          &:hover {
                            text-decoration: underline;
                          }
                        `}
                        onClick={handleProvincieClick(provincies, provincie)}
                      >
                        {provincie.name}
                      </div>
                    ))}
                  </div>
                )}
                <div
                  onClick={updateMeningen}
                  css={css`
                    cursor: pointer;
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    justify-content: flex-start;
                    color: ${colors.white};
                    margin-top: 24px;
                  `}
                >
                  <img
                    src={shuffleImage}
                    css={css`
                      width: 64px;
                      margin-right: 24px;
                    `}
                  />
                  <CTA direction="none">Shuffle</CTA>
                </div>
              </div>
            </Fragment>

            {selectedMeningId !== null && (
              <Fragment>
                <div
                  css={css`
                    position: fixed;
                    top: 0;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    display: flex;
                    padding: 50px 30px 50px 0;
                    justify-content: center;
                    align-items: center;
                    background-color: ${colors.greenSemiTransparent};
                    z-index: 999;
                    ${mobile} {
                      position: absolute;
                      padding: 20px 0 60px;
                      position: static;
                      flex: 1;
                    }
                  `}
                >
                  <div
                    css={css`
                      flex: 1;
                      display: flex;
                      justify-content: flex-end;
                      position: relative;
                      ${mobile} {
                        display: none;
                      }
                    `}
                  >
                    {selectedMeningIndex !== null && selectedMeningIndex > 0 && (
                      <CircledChevron
                        direction="left"
                        css={css`
                          cursor: pointer;
                          margin: 0;
                          padding-right: 3px;
                        `}
                        color="white"
                        hoverColor="white"
                        onClick={() => selectPreviousMening()}
                      ></CircledChevron>
                    )}
                  </div>

                  {selectedMeningIndex !== null && (
                    <div
                      css={css`
                        /* width: 55vh; */
                        position: relative;
                        width: 37%;
                        display: flex;
                        flex-direction: column;
                        padding: 50px 50px 0;
                        max-width: calc(100% - 80px);
                        ${mobile} {
                          padding: 16px;
                          max-width: 100%;
                          width: 100%;
                          display: block;
                        }
                      `}
                    >
                      <div
                        css={css`
                          display: flex;
                          justify-content: space-between;
                          margin-top: -16px;
                          margin-bottom: 16px;
                          ${desktop} {
                            display: none;
                          }
                        `}
                      >
                        <div
                          css={css`
                            font-family: Snv Cond D Fixed;
                            font-size: 30px;
                            font-style: normal;
                            font-weight: 400;
                            line-height: 35px;
                            letter-spacing: -0.02em;
                            text-align: left;
                            text-transform: uppercase;
                          `}
                        >
                          {getSelectedProvincies(provincies).length
                            ? getSelectedProvincies(provincies)
                                .map(provincie => provincie.name)
                                .join(", ")
                            : null}
                        </div>
                        <div
                          css={css`
                            font-family: Eczar;
                            font-size: 18px;
                            font-style: normal;
                            font-weight: 400;
                            line-height: 30px;
                            letter-spacing: -0.02em;
                            text-align: right;
                            color: ${colors.white};
                          `}
                        >
                          {provincieMeningen.length}{" "}
                          {provincieMeningen.length === 1
                            ? "mening"
                            : "meningen"}
                        </div>
                      </div>
                      {provincieMeningen[selectedMeningIndex] && (
                        <Fragment>
                          <div
                            css={css`
                              display: flex;
                              margin-bottom: 24px;
                              ${desktop} {
                                display: none;
                              }
                            `}
                          >
                            {provincieMeningen.map((mening, i) => (
                              <div
                                key={`streepke-${i}`}
                                css={css`
                                  flex: 1 1 6px;
                                  height: 3px;
                                  border-radius: 3px;
                                  background: ${selectedMeningIndex === i
                                    ? colors.black
                                    : colors.lightGrey};
                                  margin: 0 6px;
                                `}
                              ></div>
                            ))}
                          </div>
                          <div
                            css={css`
                              position: relative;
                              padding-top: 135%;
                              width: 100%;
                              border-radius: 20px;
                              overflow: hidden;
                              box-shadow: -5px 5px 15px 0px rgba(0, 0, 0, 0.25);
                            `}
                          >
                            <iframe
                              src={
                                provincieMeningen[selectedMeningIndex].node
                                  .shareUrl!
                              }
                              css={css`
                                position: absolute;
                                top: 0;
                                left: 0;
                                width: 100%;
                                height: 100%;
                              `}
                              frameBorder="0"
                            ></iframe>
                          </div>
                        </Fragment>
                      )}
                      <div
                        css={css`
                          display: flex;
                          justify-content: space-between;
                          padding-top: 36px;
                          ${mobile} {
                            justify-content: center;
                          }
                        `}
                      >
                        <div></div>
                        <div>
                          <Button
                            css={css`
                              width: 150px;
                            `}
                            onClick={() => setShowShare(true)}
                          >
                            Delen
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div
                    css={css`
                      flex: 1;
                      display: flex;
                      position: relative;
                      justify-content: flex-start;
                      height: 100%;
                      align-items: center;
                      ${mobile} {
                        display: none;
                      }
                    `}
                  >
                    <div
                      css={css`
                        position: absolute;
                        top: 50px;
                        left: 0px;
                        cursor: pointer;
                        color: ${colors.white};
                      `}
                      onClick={() => setSelectedMeningId(null)}
                    >
                      <H4>
                        <IoMdClose />
                      </H4>
                    </div>
                    {selectedMeningIndex !== null &&
                      meningen.length > selectedMeningIndex + 1 && (
                        <CircledChevron
                          css={css`
                            margin: 0;
                            cursor: pointer;
                            padding-left: 3px;
                          `}
                          direction="right"
                          color="white"
                          hoverColor="white"
                          onClick={() => selectNextMening()}
                        ></CircledChevron>
                      )}
                  </div>

                  {selectedMeningIndex !== null && selectedMeningIndex > 0 && (
                    <div
                      css={css`
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 20%;
                        height: 100%;
                        cursor: pointer;
                        ${desktop} {
                          display: none;
                        }
                      `}
                      onClick={() => selectPreviousMening()}
                    ></div>
                  )}
                  {selectedMeningIndex !== null &&
                    meningen.length > selectedMeningIndex + 1 && (
                      <div
                        css={css`
                          position: absolute;
                          top: 0;
                          right: 0;
                          width: 20%;
                          height: 100%;
                          cursor: pointer;
                          ${desktop} {
                            display: none;
                          }
                        `}
                        onClick={() => selectNextMening()}
                      ></div>
                    )}
                </div>
              </Fragment>
            )}
          </div>
        </div>
        {PageComponent(title, false, content, locale)}
        {showShare && selectedMeningIndex !== null && (
          <Fragment>
            <div
              css={css`
                position: fixed;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                background-color: rgba(0, 0, 0, 0.3);
                z-index: 1000;
              `}
              onClick={() => setShowShare(false)}
            ></div>
            <Section
              noReveal
              css={css`
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                z-index: 1001;
              `}
              background="black"
            >
              <H1>Delen</H1>
              <Body>Deel deze reactie met één van de volgende platforms:</Body>
              <div
                css={css`
                  margin-bottom: 16px;
                  display: flex;
                  justify-content: space-between;
                `}
              >
                <EmailShareButton
                  url={`${window.location.protocol}//${window.location.hostname}/meningen/${provincieMeningen[selectedMeningIndex].node.id}`}
                >
                  <EmailIcon />
                </EmailShareButton>
                <FacebookShareButton
                  url={`${window.location.protocol}//${window.location.hostname}/meningen/${provincieMeningen[selectedMeningIndex].node.id}`}
                >
                  <FacebookIcon />
                </FacebookShareButton>
                <TelegramShareButton
                  url={`${window.location.protocol}//${window.location.hostname}/meningen/${provincieMeningen[selectedMeningIndex].node.id}`}
                >
                  <TelegramIcon />
                </TelegramShareButton>
                <TwitterShareButton
                  url={`${window.location.protocol}//${window.location.hostname}/meningen/${provincieMeningen[selectedMeningIndex].node.id}`}
                >
                  <TwitterIcon />
                </TwitterShareButton>
                <WhatsappShareButton
                  url={`${window.location.protocol}//${window.location.hostname}/meningen/${provincieMeningen[selectedMeningIndex].node.id}`}
                >
                  <WhatsappIcon />
                </WhatsappShareButton>
              </div>
              <input
                type="text"
                value={`${window.location.protocol}//${window.location.hostname}/meningen/${provincieMeningen[selectedMeningIndex].node.id}`}
              />
            </Section>
          </Fragment>
        )}
      </Layout>
    )
  }
}

export function getSelectedProvincies(
  provincies: { name: string; selected: boolean }[]
) {
  return provincies.filter(provincie => provincie.selected)
}
